import { useMemo } from 'react';

import { useAppConfig } from '@common/app-config';
import { formatCategoryList, useInitLikedProducts, useLikeProductService } from '@widgets/like-product';
import { UxGoodsCardItemForVerticalProductCard as UxGoodsCardItem } from '@common/product-card';
import {
  parseQueryString,
  PDPGoodsGroup_goods_carousel_component_list as ProductCardItem,
  usePdpBaseInfo,
  usePdpRelatedProductRecommendGroups,
} from '@domains/pdp-common';

import { useTopRecommendListLog } from '../log/useTopRecommendListLog';

const convertTopAreaList = (list: ProductCardItem[]) => list.map(({ catalog_product_id }) => catalog_product_id || '');

interface TopRecommendListProps {
  productId: string;
}

export const useTopRecommendListService = ({ productId }: TopRecommendListProps) => {
  const { isZigzagApp } = useAppConfig();
  const {
    flags: { is_selected_shop },
  } = usePdpBaseInfo({ catalog_product_id: productId });
  const { top_area, isFetched } = usePdpRelatedProductRecommendGroups({
    catalog_product_id: productId,
    isSelectedShop: Boolean(is_selected_shop && isZigzagApp),
  });

  const { impressionProductItem, clickProductItem, savedProduct } = useTopRecommendListLog(productId);
  const { onLikeProduct } = useLikeProductService();

  const memoizedTopArea = useMemo(() => top_area, [isFetched]);

  const memoizedTopAreaItemList = useMemo(
    () => top_area.flatMap((item) => item.goods_carousel.component_list) || [],
    [top_area],
  );

  useInitLikedProducts({
    data: memoizedTopAreaItemList,
    convert: convertTopAreaList,
  });

  const onImpressionProduct = (item: UxGoodsCardItem, position: number) => impressionProductItem(item, position);

  const onClickProduct = (item: UxGoodsCardItem, position: number) => clickProductItem(item, position);

  const onSavedProduct = (checked: boolean, item: UxGoodsCardItem, position: number) => {
    const inAppLikeProductLog = {
      object_idx: position,
      object_section: 'top_recommendation',
      ...{
        original_catalog_product_id: productId,
        ...(item.log ? parseQueryString(item.log) : undefined),
      },
    };

    const inAppLikeProductPayload = {
      like: checked,
      browsingType: item.browsing_type,
      shopId: Number(item.shop_id),
      shopProductNo: item.shop_product_no,
      catalogProductId: item.catalog_product_id,
      title: item.title || '',
      goodsUrl: item.product_url || '',
      imageUrl: item.image_url || '',
      price: Number(item.final_price),
      discountRate: Number(item.discount_rate),
      isZpay: Boolean(item.zpay),
      isFreeShipping: Boolean(item.free_shipping),
      log: inAppLikeProductLog,
    };

    const airbridge = {
      brandID: item.shop_id,
      brandName: item.shop_name,
      productID: item.catalog_product_id,
      productName: item.title,
      price: item.final_price,
      categoryID: formatCategoryList(item.managed_category_list, 'id'),
      categoryName: formatCategoryList(item.managed_category_list, 'value'),
    };

    const webLikeProductPayload = {
      checked,
      productData: { catalog_product_id: item.catalog_product_id || '', shop_id: item.shop_id || '' },
      airbridge,
      sendLog: (checked: boolean) =>
        savedProduct({ category: checked ? 'save_product' : 'remove_saved_product', item, position }),
    };

    onLikeProduct({ inAppLikeProductPayload, webLikeProductPayload });
  };

  return {
    isHide: !memoizedTopAreaItemList?.length,
    list: memoizedTopArea,
    onImpressionProduct,
    onClickProduct,
    onSavedProduct,
  };
};
